import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Route, Switch, useRouteMatch, useHistory, Redirect } from 'react-router-dom';
import Copyright from '../shared/util/Copyright';
import { useDispatch } from 'react-redux';
import { getUserDetails, selectAuthState, selectCurrentUser } from '../shared/sagas/auth/authSlice';
import ProfileSettings from '../shared/profileSettings/profileSettings';
import { mdTheme } from './styles/ownerUI';
import Documents from './documents';
import LeftDrawerPanel from './components/LeftDrawerPanel';
import PageHeader from './components/PageHeader';
import { Stack } from '@mui/material';
import { getFetchingOwnerDetails, selectOwnersState } from '../pmui/owners/sagas/ownersSlice';
import { getCompany } from '../shared/sagas/propertyGroups/propertyGroupsSlice';
import OwnerLedger from './ledger/OwnerLedger';

type Anchor = 'left';

export default function OwnerUI() {
  const [drawerOpen, setDrawerOpen] = React.useState(true);
  let { path } = useRouteMatch();
  const history = useHistory();
  const dispatch = useDispatch();
  const [deviceWidth, changeDeviceSize] = React.useState(window.innerWidth);
  const currentUser = useSelector(selectCurrentUser);
  const { userDetails } = useSelector(selectAuthState);
  const { isFetchingOwnerDetailsSuccess, OwnerDetails } = useSelector(selectOwnersState);
  const mainRef = React.useRef(null);
  const [headerSubtitle, setHeaderSubtitle] = useState('');
  const [state, setState] = React.useState({
    left: false,
  });

  useEffect(() => {
    if (!currentUser?.accessToken) {
      history.push('/login');
    }
    dispatch(getUserDetails());
    const resizeW = () => changeDeviceSize(window.innerWidth);
    window.addEventListener('resize', resizeW);
    return () => window.removeEventListener('resize', resizeW);
  }, [currentUser, history, deviceWidth]);

  useEffect(() => {
    if (userDetails && currentUser) {
      const profileOfInterest = userDetails.Profiles?.find((p) => p.Uuid === currentUser.currentProfile.uuid);
      if (profileOfInterest) {
        setHeaderSubtitle(profileOfInterest.DisplayName);
      }
    }
  }, [userDetails]);

  useEffect(() => {
    if (currentUser?.currentProfile.uuid) {
      dispatch({
        type: getFetchingOwnerDetails.type,
        payload: currentUser?.currentProfile.uuid,
      });
    }
  }, [currentUser?.currentProfile.uuid]);

  useEffect(() => {
    if (isFetchingOwnerDetailsSuccess) {
      dispatch({
        type: getCompany.type,
        payload: {
          companyUuid: OwnerDetails.CompanyUuid,
        },
      });
    }
  }, [isFetchingOwnerDetailsSuccess]);

  const toggleMobileDrawer = (anchor: Anchor, open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
    if (event.type === 'keydown' && ((event as React.KeyboardEvent).key === 'Tab' || (event as React.KeyboardEvent).key === 'Shift')) {
      return;
    }

    setState({ ...state, [anchor]: open });
  };

  return (
    <ThemeProvider theme={mdTheme}>
      <Stack direction="row">
        <CssBaseline />
        <LeftDrawerPanel open={drawerOpen} setOpen={setDrawerOpen} state={state} toggleMobileDrawer={toggleMobileDrawer} />
        <Stack
          component="main"
          padding="24px"
          gap="24px"
          height="100vh"
          overflow="auto"
          flexGrow="1"
          sx={{
            backgroundColor: (theme) => (theme.palette.mode === 'light' ? theme.palette.grey[100] : theme.palette.grey[900]),
          }}
          ref={mainRef}
        >
          <PageHeader state={state} subTitle={headerSubtitle} toggleMobileDrawer={toggleMobileDrawer} />
          <Switch>
            <Route exact path={path}>
              <Redirect to={`${path}/ledger`} />
            </Route>
            <Route path={`${path}/documents`} component={Documents} />
            <Route path={`${path}/ledger`} component={OwnerLedger} />
            <Route path={`${path}/profilesettings`} component={ProfileSettings} />
          </Switch>

          <Copyright sx={{ pt: 0, mt: 'auto' }} />
        </Stack>
      </Stack>
    </ThemeProvider>
  );
}
